import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Lock, RefreshCw, CheckCircle, Clock, Building2,
  DollarSign, Database, Cpu, AlertCircle, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CRAWLER_API = process.env.REACT_APP_CRAWLER_URL || 'https://meridian-crawler.onrender.com/api';

function authHeader(password) {
  return { Authorization: 'Basic ' + btoa('admin:' + password) };
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toasts, onRemove }) {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`flex items-center gap-3 px-4 py-3 text-sm font-medium shadow-lg border
            ${t.type === 'success'
              ? 'bg-white border-emerald-300 text-emerald-800'
              : 'bg-white border-red-300 text-red-700'}`}
        >
          {t.type === 'success' ? <CheckCircle size={15} /> : <AlertCircle size={15} />}
          {t.message}
          <button onClick={() => onRemove(t.id)} className="ml-2 opacity-50 hover:opacity-100">
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#A8B4B8] p-5 flex items-start gap-4">
      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${accent || 'bg-[#0D3D4A]/10'}`}>
        <Icon size={18} className="text-[#0D3D4A]" />
      </div>
      <div>
        <p className="text-2xl font-semibold text-stone-900">
          {value === null || value === undefined ? '—' : value.toLocaleString()}
        </p>
        <p className="text-xs text-stone-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-lg font-semibold text-[#0D3D4A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {title}
        </h2>
        {subtitle && <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Auth Gate ────────────────────────────────────────────────────────────────
function AuthGate({ onAuth }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await axios.get(`${API}/admin/dashboard/stats`, { headers: authHeader(pw) });
      onAuth(pw);
    } catch {
      setErr('Invalid admin password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <div className="w-12 h-12 bg-[#0D3D4A] flex items-center justify-center mb-6">
          <Lock size={20} className="text-white" />
        </div>
        <h1 className="text-2xl font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Admin Dashboard
        </h1>
        <p className="text-sm text-stone-500 mb-6">Enter your admin password to continue.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={pw}
            onChange={e => setPw(e.target.value)}
            placeholder="Admin password"
            className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
            autoFocus
            required
          />
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0D3D4A] text-white py-3 text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-60"
          >
            {loading ? 'Verifying…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
function Dashboard({ password }) {
  const [stats, setStats]             = useState(null);
  const [pending, setPending]         = useState([]);
  const [billing, setBilling]         = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [toasts, setToasts]           = useState([]);
  const [approving, setApproving]     = useState({});
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [crawlerSecret, setCrawlerSecret]   = useState('');
  const [crawlerLoading, setCrawlerLoading] = useState(false);
  const [crawlerStatus, setCrawlerStatus]   = useState(null);

  const hdrs = authHeader(password);

  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };
  const removeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const loadAll = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsRes, pendingRes, billingRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, { headers: hdrs }),
        axios.get(`${API}/admin/providers/pending`, { headers: hdrs }),
        axios.get(`${API}/admin/billing/pending`, { headers: hdrs }),
      ]);
      setStats(statsRes.data);
      setPending(pendingRes.data);
      setBilling(billingRes.data.providers || []);
    } catch (err) {
      addToast('Failed to load dashboard data.', 'error');
    } finally {
      setStatsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    axios.get(`${CRAWLER_API}/crawler/coverage`)
      .then(res => setCrawlerStatus(res.data))
      .catch(() => {}); // non-critical, fail silently
  }, []);

  const approveProvider = async (providerId, providerName) => {
    setApproving(prev => ({ ...prev, [providerId]: true }));
    try {
      await axios.patch(
        `${API}/admin/providers/${providerId}/approve`,
        {},
        { headers: hdrs }
      );
      setPending(prev => prev.filter(p => p.id !== providerId));
      setStats(prev => prev ? {
        ...prev,
        pending_review: Math.max(0, prev.pending_review - 1),
        active_providers: prev.active_providers + 1,
      } : prev);
      addToast(`${providerName} approved and now live.`);
    } catch {
      addToast(`Failed to approve ${providerName}.`, 'error');
    } finally {
      setApproving(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const triggerBilling = async () => {
    setTriggerLoading(true);
    try {
      await axios.post(`${API}/admin/billing/trigger`, {}, { headers: hdrs });
      addToast('Billing job completed. Check server logs for details.');
      await loadAll();
    } catch {
      addToast('Billing trigger failed.', 'error');
    } finally {
      setTriggerLoading(false);
    }
  };

  const runCrawler = async () => {
    if (!crawlerSecret.trim()) { addToast('Enter the crawler secret first.', 'error'); return; }
    setCrawlerLoading(true);
    try {
      await axios.post(
        `${CRAWLER_API}/crawler/run-now`,
        {},
        { headers: { 'X-Crawler-Secret': crawlerSecret } }
      );
      addToast('Crawler job triggered successfully.');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Crawler trigger failed.', 'error');
    } finally {
      setCrawlerLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      <Toast toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="bg-[#0D3D4A] px-4 sm:px-8 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs tracking-widest uppercase font-semibold text-[#A8B4B8] mb-1">Meridian</p>
            <h1 className="text-2xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
          </div>
          <button
            onClick={loadAll}
            disabled={statsLoading}
            className="flex items-center gap-2 text-xs text-[#A8B4B8] hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={statsLoading ? 'animate-spin' : ''} />
            {statsLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-10">

        {/* ── Section 1: Stats ─────────────────────────────────────────── */}
        <div>
          <SectionHeader title="Platform Overview" subtitle={stats ? `Last updated ${formatDate(stats.last_updated)}` : ''} />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard label="Total Providers"   value={stats?.total_providers}      icon={Building2} />
            <StatCard label="Pending Review"    value={stats?.pending_review}       icon={Clock}
              accent={stats?.pending_review > 0 ? 'bg-amber-100' : undefined} />
            <StatCard label="Active Providers"  value={stats?.active_providers}     icon={CheckCircle} />
            <StatCard label="Price Records"     value={stats?.total_price_records}  icon={Database} />
            <StatCard label="Unbilled Leads"    value={stats?.total_leads_unbilled} icon={DollarSign}
              accent={stats?.total_leads_unbilled > 0 ? 'bg-amber-100' : undefined} />
            <StatCard label="Crawler Targets"   value={stats?.crawler_targets}      icon={Cpu} />
          </div>
        </div>

        {/* ── Section 2: Pending Provider Registrations ────────────────── */}
        <div>
          <SectionHeader
            title="Pending Provider Registrations"
            subtitle={`${pending.length} provider${pending.length !== 1 ? 's' : ''} awaiting approval`}
          />
          {pending.length === 0 ? (
            <div className="bg-white border border-[#A8B4B8] p-8 text-center text-sm text-stone-400">
              No pending registrations.
            </div>
          ) : (
            <div className="bg-white border border-[#A8B4B8] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#A8B4B8] bg-[#F0EEE9]">
                    {['Provider Name', 'Type', 'City, State', 'Contact Email', 'Submitted', 'Action'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-stone-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {pending.map(p => (
                    <tr key={p.id} className="hover:bg-[#F0EEE9]/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-stone-900">{p.name}</td>
                      <td className="px-4 py-3 text-stone-600">{p.type || '—'}</td>
                      <td className="px-4 py-3 text-stone-600">{p.city}{p.state ? `, ${p.state}` : ''}</td>
                      <td className="px-4 py-3 text-stone-500 font-mono text-xs">{p.registration_email || '—'}</td>
                      <td className="px-4 py-3 text-stone-400 text-xs">{formatDate(p.created_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => approveProvider(p.id, p.name)}
                          disabled={!!approving[p.id]}
                          className="px-3 py-1.5 bg-[#0D3D4A] text-white text-xs font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                        >
                          {approving[p.id] ? (
                            <RefreshCw size={11} className="animate-spin" />
                          ) : (
                            <CheckCircle size={11} />
                          )}
                          {approving[p.id] ? 'Approving…' : 'Approve'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Section 3: Billing Overview ──────────────────────────────── */}
        <div>
          <SectionHeader
            title="Billing Overview"
            subtitle={`${billing.length} provider${billing.length !== 1 ? 's' : ''} with unbilled leads`}
            action={
              <button
                onClick={triggerBilling}
                disabled={triggerLoading}
                className="flex items-center gap-2 px-4 py-2 bg-[#0D3D4A] text-white text-xs font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-50"
              >
                <DollarSign size={12} />
                {triggerLoading ? 'Running…' : 'Trigger Billing'}
              </button>
            }
          />
          {billing.length === 0 ? (
            <div className="bg-white border border-[#A8B4B8] p-8 text-center text-sm text-stone-400">
              No providers with unbilled leads.
            </div>
          ) : (
            <div className="bg-white border border-[#A8B4B8] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#A8B4B8] bg-[#F0EEE9]">
                    {['Provider Name', 'Unbilled Leads', 'Outstanding ($)', 'Stripe Customer', 'Status'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-stone-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {billing.map(p => (
                    <tr key={p.provider_id} className="hover:bg-[#F0EEE9]/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-stone-900">{p.name}</td>
                      <td className="px-4 py-3 text-stone-700 font-semibold">{p.lead_count_billable}</td>
                      <td className="px-4 py-3 text-stone-700 font-semibold">
                        ${p.outstanding_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-stone-400">
                        {p.stripe_customer_id || '—'}
                      </td>
                      <td className="px-4 py-3">
                        {p.has_stripe_customer ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                            <CheckCircle size={11} /> Ready to bill
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">
                            <AlertCircle size={11} /> No Stripe customer
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Section 4: Crawler Control ───────────────────────────────── */}
        <div>
          <SectionHeader
            title="Crawler Control"
            subtitle={`${stats?.crawler_targets ?? '—'} targets configured`}
          />
          <div className="bg-white border border-[#A8B4B8] p-6">
            <p className="text-sm text-stone-500 mb-4">
              Manually trigger a crawler run. Enter the crawler secret (set as{' '}
              <code className="text-xs bg-stone-100 px-1 py-0.5">INTERNAL_SECRET</code> on the crawler service).
            </p>
            <div className="flex gap-3 items-end">
              <div className="flex-1 max-w-sm">
                <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">
                  Crawler Secret
                </label>
                <input
                  type="password"
                  value={crawlerSecret}
                  onChange={e => setCrawlerSecret(e.target.value)}
                  placeholder="X-Crawler-Secret value"
                  className="w-full px-4 py-2.5 border border-[#A8B4B8] bg-white text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                />
              </div>
              <button
                onClick={runCrawler}
                disabled={crawlerLoading}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#0D3D4A] text-white text-sm font-semibold hover:bg-[#0E4557] transition-colors disabled:opacity-50"
              >
                <Cpu size={14} />
                {crawlerLoading ? 'Running…' : 'Run Crawler Now'}
              </button>
            </div>
            <div className="mt-4 border-t border-stone-100 pt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-stone-400 text-xs uppercase tracking-wider">Crawler Targets</span>
                <p className="font-semibold text-stone-800 mt-0.5">{stats?.crawler_targets ?? '—'}</p>
              </div>
              <div>
                <span className="text-stone-400 text-xs uppercase tracking-wider">Hospitals with Price Data</span>
                <p className="font-semibold text-stone-800 mt-0.5">{stats?.hospitals_with_data ?? '—'}</p>
              </div>
            </div>

            {/* Crawl Coverage */}
            {crawlerStatus && (
              <div className="mt-4 border-t border-stone-100 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Crawl Coverage</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-stone-400 text-xs">Total Targets</span>
                    <p className="font-semibold text-stone-800 mt-0.5">{crawlerStatus.total_targets}</p>
                  </div>
                  <div>
                    <span className="text-stone-400 text-xs">Never Crawled</span>
                    <p className="font-semibold text-stone-800 mt-0.5">{crawlerStatus.never_crawled}</p>
                  </div>
                  <div>
                    <span className="text-stone-400 text-xs">Crawled This Week</span>
                    <p className="font-semibold text-stone-800 mt-0.5">{crawlerStatus.crawled_this_week}</p>
                  </div>
                  <div>
                    <span className="text-stone-400 text-xs">Last Run</span>
                    <p className="font-semibold text-stone-800 mt-0.5">
                      {(crawlerStatus.last_run_records || 0).toLocaleString()} records
                      <span className="ml-1 text-xs text-stone-400">({crawlerStatus.last_run_status})</span>
                    </p>
                  </div>
                </div>
                {(() => {
                  const pct = crawlerStatus.total_targets > 0
                    ? Math.round(((crawlerStatus.total_targets - crawlerStatus.never_crawled) / crawlerStatus.total_targets) * 100)
                    : 0;
                  return (
                    <>
                      <div className="w-full bg-stone-200 rounded-full h-2">
                        <div
                          className="bg-[#0D3D4A] h-2 rounded-full transition-all"
                          style={{ width: pct + '%' }}
                        />
                      </div>
                      <p className="text-xs text-stone-400 mt-1">{pct}% of targets crawled at least once</p>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Page Entry Point ─────────────────────────────────────────────────────────
export default function AdminDashboardPage() {
  const [password, setPassword] = useState(null);
  if (!password) return <AuthGate onAuth={setPassword} />;
  return <Dashboard password={password} />;
}
